import { useRef } from 'react';
import useViewportGestures from '../../hooks/useViewportGestures';

/**
 * A live camera's screen, with the gestures its host allows (2026-10-21;
 * docs/FIELD_AI_VISION_SPEC.md section 7). It is the element everything of
 * the camera sits in -- the picture, the chips, the bar -- so a swipe in
 * from an edge counts wherever it starts; hooks/useViewportGestures.js reads
 * the strokes.
 *
 *   <CameraViewport edges={['left', 'right', 'bottom']} enabled={!sheet}
 *     onEdgeSwipe={openSheetFor} style={...} data-testid="field-camera">
 *     <video ... onClick={tapFocus.onTap} />
 *     ...
 *   </CameraViewport>
 *
 * `edges` names the edges this host has a sheet for; `lasso` whether a loop
 * drawn on the picture is read (the encircle, phase A3). A host that allows
 * nothing passes neither, and a stroke is then just what it always was.
 * `onPinch` takes a two-finger pinch (the camera's zoom: useCameraControls'
 * `onPinch`, 2026-10-22), read while `pinch` (default `enabled`). The dish
 * and microscope cameras put only their picture in one, for the pinch.
 *
 * touch-action is none here, so a finger's drag is the page's to read rather
 * than the browser's to scroll or zoom. Anything inside that scrolls (the
 * photo tray, a sheet) is its own scroll container, and scrolls as before.
 */
export default function CameraViewport({
  edges = [], lasso = false, enabled = true, onEdgeSwipe, onLasso, pinch = enabled, onPinch, style, children, ...rest
}) {
  const ref = useRef(null);
  useViewportGestures(ref, { enabled, edges, lasso, onEdgeSwipe, onLasso, pinch, onPinch: onPinch || undefined });
  return (
    <div ref={ref} style={{ ...style, touchAction: 'none' }} data-gestures={edges.join(' ')}
         data-pinch={onPinch ? 'zoom' : undefined} {...rest}>
      {children}
    </div>
  );
}
